/** Debug overlay rendering a static bottom-bar legend mapping archetype names to their colors. */
import Phaser from 'phaser';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { crispText, crispRect } from '../../ui/CrispUtil';
import { TextStyle, Color } from '../../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y } from '../../utils/Constants';

/** Draws a one-time static legend bar at the bottom of the screen; rebuilt only when cleared. */
export class LegendOverlay {
  private scene: Phaser.Scene;
  private elements: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Create the legend bar if it doesn't already exist (idempotent per enable cycle). */
  draw(_graphics: Phaser.GameObjects.Graphics): void {
    if (this.elements.length > 0) return; // legend is static, don't recreate every frame
    this.create();
  }

  private create(): void {
    this.clear();

    const barHeight = 16;
    const y = SF0_Y + GAME_HEIGHT - barHeight;

    const bg = crispRect(this.scene, SF0_X + GAME_WIDTH / 2, y + barHeight / 2, GAME_WIDTH, barHeight, Color.black, 0.7);
    bg.setScrollFactor(0).setDepth(2000);
    this.elements.push(bg);

    const entries = ConfigLoader.enemies.map(e => [e.id, ConfigLoader.getArchetypeColor(e.id)] as [string, number]);
    const entryWidth = 80;
    const totalWidth = entries.length * entryWidth;
    let x = SF0_X + (GAME_WIDTH - totalWidth) / 2 + 8;

    for (const [name, color] of entries) {
      const swatch = crispRect(this.scene, x, y + barHeight / 2, 8, 8, color);
      swatch.setScrollFactor(0).setDepth(2001);
      this.elements.push(swatch);

      const label = crispText(this.scene, x + 8, y + barHeight / 2, name,
        TextStyle.debugPanel,
      ).setOrigin(0, 0.5).setScrollFactor(0).setDepth(2001);
      this.elements.push(label);

      x += entryWidth;
    }
  }

  /** Destroy all legend elements, allowing a fresh rebuild on the next draw. */
  clear(): void {
    for (const el of this.elements) el.destroy();
    this.elements = [];
  }

  /** Destroy all objects and free resources. */
  destroy(): void {
    this.clear();
  }
}
